import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { liveQuery } from 'dexie';
import { from, of, switchMap } from 'rxjs';
import {
  Budget,
  db,
  DbCashItem,
  DbUserSettings,
  DebtPayoffPlan,
  InstanceOverride,
} from '../db/app-db';
import { AuthService } from './auth.service';
import { CloudBackupService } from './cloud-backup.service';
import { MessageService } from './message.service';

export interface SplitPaymentPart {
  amount: number;
  date: string;
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private cloudBackup = inject(CloudBackupService);
  private auth = inject(AuthService);
  private messageService = inject(MessageService);

  readonly budgets: Signal<Budget[]> = toSignal(from(liveQuery(() => db.budgets.toArray())), {
    initialValue: [],
  });

  private activeBudgetIdSignal = signal<string | null>(this.getStoredBudgetId());
  readonly activeBudgetId = computed(() => this.activeBudgetIdSignal());

  private activeBudgetId$ = toObservable(this.activeBudgetId);

  readonly activeBudget = computed(() => {
    const list = this.budgets();
    const activeId = this.activeBudgetIdSignal();
    return list.find((b) => b.id === activeId) || list[0] || null;
  });

  readonly activeCashItems: Signal<DbCashItem[]> = toSignal(
    this.activeBudgetId$.pipe(
      switchMap((activeId) => {
        if (!activeId) return of([]);
        return liveQuery(() => db.cashItems.where({ budgetId: activeId }).toArray());
      }),
    ),
    { initialValue: [] },
  );

  // Filter out auto-split / manual-split child items from master setup lists
  readonly activeIncomes = computed(() =>
    this.activeCashItems()
      .filter((item) => item.type === 'income' && !(item as any).splitParentDate)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
  );

  readonly activeBills = computed(() =>
    this.activeCashItems()
      .filter((item) => item.type === 'expense' && !(item as any).splitParentDate)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
  );

  readonly activeSettings: Signal<DbUserSettings | null> = toSignal(
    this.activeBudgetId$.pipe(
      switchMap((activeId) => {
        if (!activeId) return of(null);
        return liveQuery(async () => {
          const settings = await db.userSettings.where({ budgetId: activeId }).first();
          return settings ?? null;
        });
      }),
    ),
    { initialValue: null },
  );

  readonly activeOverrides: Signal<InstanceOverride[]> = toSignal(
    this.activeBudgetId$.pipe(
      switchMap((activeId) => {
        if (!activeId) return of([]);
        return liveQuery(() => db.instanceOverrides.where({ budgetId: activeId }).toArray());
      }),
    ),
    { initialValue: [] },
  );

  private async triggerBackgroundSync(): Promise<void> {
    const isCloudBackupEnabled = localStorage.getItem('cloud_backup_opt_in') === 'true';
    if (!isCloudBackupEnabled) return;

    const user = this.auth.currentUser();
    const googleSub = user?.id;
    if (!googleSub) return;

    // Retrieve the timestamp of when this device last synced/loaded data
    const lastSyncTimestamp =
      localStorage.getItem('slt_last_sync_timestamp') || new Date(0).toISOString();

    try {
      const backupPayload = {
        version: 4,
        exportedAt: new Date().toISOString(),
        budgets: await db.budgets.toArray(),
        cashItems: await db.cashItems.toArray(),
        instanceOverrides: await db.instanceOverrides.toArray(),
        userSettings: await db.userSettings.toArray(),
      };

      this.cloudBackup.saveBackup(googleSub, backupPayload, lastSyncTimestamp).subscribe({
        next: () => {
          // Update local sync baseline to now upon successful push
          localStorage.setItem('slt_last_sync_timestamp', backupPayload.exportedAt);
        },
        error: (err) => {
          if (err.status === 409) {
            console.warn('Backup conflict detected: Another device has newer changes.');
          } else {
            console.debug('Background cloud backup sync deferred:', err);
          }
        },
      });
    } catch (err) {
      console.debug('Failed to compile background backup payload:', err);
    }
  }

  selectBudget(id: string): void {
    localStorage.setItem('slt_active_budget_id', id);
    this.activeBudgetIdSignal.set(id);
  }

  async createBudget(name: string, userId?: string): Promise<Budget> {
    const id = crypto.randomUUID();
    const isFirst = (await db.budgets.count()) === 0;

    const newBudget: Budget = {
      id,
      userId,
      name,
      isDefault: isFirst,
      createdAt: new Date().toISOString(),
    };

    const defaultSettings: DbUserSettings = {
      id: crypto.randomUUID(),
      budgetId: id,
      userId,
      payPeriodAnchorDate: new Date().toISOString().split('T')[0],
      payPeriodDurationDays: 14,
      lowBalanceThreshold: 50,
      enableCarryover: false,
    };

    await db.transaction('rw', db.budgets, db.userSettings, async () => {
      await db.budgets.add(newBudget);
      await db.userSettings.add(defaultSettings);
    });

    this.selectBudget(id);
    await this.triggerBackgroundSync();
    return newBudget;
  }

  async updateBudgetName(budgetId: string, newName: string): Promise<void> {
    await db.budgets.update(budgetId, { name: newName.trim() });
    await this.triggerBackgroundSync();
  }

  async deleteBudget(budgetId: string): Promise<void> {
    await db.transaction(
      'rw',
      db.budgets,
      db.cashItems,
      db.instanceOverrides,
      db.userSettings,
      async () => {
        await db.cashItems.where({ budgetId }).delete();
        await db.instanceOverrides.where({ budgetId }).delete();
        await db.userSettings.where({ budgetId }).delete();
        await db.budgets.delete(budgetId);
      },
    );

    if (this.activeBudgetIdSignal() === budgetId) {
      const remaining = await db.budgets.toArray();
      if (remaining.length > 0) {
        this.selectBudget(remaining[0].id);
      } else {
        localStorage.removeItem('slt_active_budget_id');
        this.activeBudgetIdSignal.set(null);
      }
    }
    await this.triggerBackgroundSync();
  }

  async addCashItem(item: Omit<DbCashItem, 'id'>): Promise<string> {
    const id = crypto.randomUUID();
    await db.cashItems.add({ ...item, id });
    await this.triggerBackgroundSync();
    return id;
  }

  async updateCashItem(id: string, updates: Partial<DbCashItem>): Promise<void> {
    await db.cashItems.update(id, updates);
    await this.triggerBackgroundSync();
  }

  async updateInstanceAmount(
    cashItemId: string,
    projectedDate: string,
    newAmount: number,
  ): Promise<void> {
    const budgetId = this.activeBudgetId();
    if (!budgetId) return;

    const key = `${cashItemId}_${projectedDate}`;
    const existing = await db.instanceOverrides.get(key);

    if (existing) {
      await db.instanceOverrides.update(key, { actualAmount: newAmount });
    } else {
      await db.instanceOverrides.add({
        id: key,
        cashItemId,
        budgetId,
        projectedDate,
        isPaid: false,
        actualAmount: newAmount,
      });
    }
    await this.triggerBackgroundSync();
  }

  async deleteCashItem(id: string): Promise<void> {
    await db.transaction('rw', db.cashItems, db.instanceOverrides, async () => {
      await db.instanceOverrides.where({ cashItemId: id }).delete();
      await db.cashItems.delete(id);
    });
    await this.triggerBackgroundSync();
  }

  async deleteCashItems(itemIds: string[]): Promise<void> {
    if (!itemIds || itemIds.length === 0) return;
    await db.cashItems.bulkDelete(itemIds);
    await this.triggerBackgroundSync();
  }

  async moveInstance(cashItemId: string, originalDate: string, newDate: string): Promise<void> {
    const budgetId = this.activeBudgetId();
    if (!budgetId) return;

    const key = `${cashItemId}_${originalDate}`;
    const existing = await db.instanceOverrides.get(key);

    await db.instanceOverrides.put({
      id: key,
      cashItemId,
      budgetId,
      projectedDate: originalDate,
      isPaid: existing?.isPaid ?? false,
      isSkipped: existing?.isSkipped,
      isLocked: existing?.isLocked,
      actualAmount: existing?.actualAmount,
      actualDate: newDate,
    });
    await this.triggerBackgroundSync();
  }

  async splitInstanceMultiple(
    item: {
      id: string;
      name: string;
      type: 'income' | 'expense';
      category?: any;
      isLoan?: boolean;
    },
    originalDate: string,
    splits: SplitPaymentPart[],
  ): Promise<void> {
    const budgetId = this.activeBudgetId();
    if (!budgetId || splits.length < 2) return;

    await db.transaction('rw', db.cashItems, db.instanceOverrides, async () => {
      const key = `${item.id}_${originalDate}`;
      const existing = await db.instanceOverrides.get(key);
      if (existing?.splitChildItemIds?.length) {
        await db.cashItems.bulkDelete(existing.splitChildItemIds);
      }

      const [part1, ...additionalParts] = splits;
      const childIds: string[] = [];

      for (let i = 0; i < additionalParts.length; i++) {
        const part = additionalParts[i];
        const childId = crypto.randomUUID();
        childIds.push(childId);

        await db.cashItems.add({
          id: childId,
          budgetId,
          name: `${item.name} (Split ${i + 2}/${splits.length})`,
          amount: part.amount,
          type: item.type,
          category: item.category,
          isLoan: item.isLoan,
          frequency: 'once',
          startDate: part.date,
          splitParentDate: originalDate,
        } as DbCashItem);
      }

      await db.instanceOverrides.put({
        id: key,
        cashItemId: item.id,
        budgetId,
        projectedDate: originalDate,
        isPaid: existing?.isPaid ?? false,
        isSkipped: existing?.isSkipped,
        isLocked: existing?.isLocked,
        actualAmount: part1.amount,
        actualDate: part1.date !== originalDate ? part1.date : existing?.actualDate,
        splitChildItemIds: childIds,
      });
    });
    await this.triggerBackgroundSync();
  }

  async resetInstanceOverride(cashItemId: string, originalDate: string): Promise<void> {
    const key = `${cashItemId}_${originalDate}`;
    const override = await db.instanceOverrides.get(key);

    await db.transaction('rw', db.cashItems, db.instanceOverrides, async () => {
      if (override?.splitChildItemIds?.length) {
        await db.cashItems.bulkDelete(override.splitChildItemIds);
      }
      await db.instanceOverrides.delete(key);
    });
    await this.triggerBackgroundSync();
  }

  async toggleInstancePaid(
    cashItemId: string,
    projectedDate: string,
    isPaid: boolean,
  ): Promise<void> {
    const budgetId = this.activeBudgetId();
    if (!budgetId) return;

    const key = `${cashItemId}_${projectedDate}`;
    const existing = await db.instanceOverrides.get(key);

    if (existing) {
      await db.instanceOverrides.update(key, { isPaid });
    } else {
      await db.instanceOverrides.add({
        id: key,
        cashItemId,
        budgetId,
        projectedDate,
        isPaid,
      });
    }
    await this.triggerBackgroundSync();
  }

  async toggleInstanceSkipped(
    cashItemId: string,
    projectedDate: string,
    isSkipped: boolean,
  ): Promise<void> {
    const budgetId = this.activeBudgetId();
    if (!budgetId) return;

    const key = `${cashItemId}_${projectedDate}`;
    const existing = await db.instanceOverrides.get(key);

    if (existing) {
      await db.instanceOverrides.update(key, { isSkipped });
    } else {
      await db.instanceOverrides.add({
        id: key,
        cashItemId,
        budgetId,
        projectedDate,
        isPaid: false,
        isSkipped,
      });
    }
    await this.triggerBackgroundSync();
  }

  async toggleInstanceLocked(
    cashItemId: string,
    projectedDate: string,
    isLocked: boolean,
  ): Promise<void> {
    const budgetId = this.activeBudgetId();
    if (!budgetId) return;

    const key = `${cashItemId}_${projectedDate}`;
    const existing = await db.instanceOverrides.get(key);

    if (existing) {
      await db.instanceOverrides.update(key, { isLocked });
    } else {
      await db.instanceOverrides.add({
        id: key,
        cashItemId,
        budgetId,
        projectedDate,
        isPaid: false,
        isLocked,
      });
    }
    await this.triggerBackgroundSync();
  }

  async deleteOverrides(overrideIds: string[]): Promise<void> {
    if (!overrideIds || overrideIds.length === 0) return;
    await db.instanceOverrides.bulkDelete(overrideIds);
    await this.triggerBackgroundSync();
  }

  async resetForwardSchedule(
    budgetId: string,
    todayStr: string,
  ): Promise<{ overridesCleared: number; splitsRemoved: number }> {
    const overrides = await db.instanceOverrides.where('budgetId').equals(budgetId).toArray();

    const overrideIdsToDelete: string[] = [];
    const childItemIdsToDelete: string[] = [];

    for (const ov of overrides) {
      const targetDate = ov.actualDate || ov.projectedDate;

      if (ov.isLocked || ov.isPaid) {
        continue;
      }

      if (ov.projectedDate >= todayStr || targetDate >= todayStr) {
        overrideIdsToDelete.push(ov.id);

        if (ov.splitChildItemIds && ov.splitChildItemIds.length > 0) {
          childItemIdsToDelete.push(...ov.splitChildItemIds);
        }
      }
    }

    if (overrideIdsToDelete.length > 0) {
      await db.instanceOverrides.bulkDelete(overrideIdsToDelete);
    }

    if (childItemIdsToDelete.length > 0) {
      await db.cashItems.bulkDelete(childItemIdsToDelete);
    }

    await this.triggerBackgroundSync();

    return {
      overridesCleared: overrideIdsToDelete.length,
      splitsRemoved: childItemIdsToDelete.length,
    };
  }

  async getSettingsForBudget(budgetId: string): Promise<DbUserSettings | undefined> {
    return db.userSettings.where({ budgetId }).first();
  }

  async saveSettings(settings: DbUserSettings): Promise<void> {
    await db.userSettings.put(settings);
    await this.triggerBackgroundSync();
  }

  async exportBackup(): Promise<void> {
    const data = {
      version: 4,
      exportedAt: new Date().toISOString(),
      budgets: await db.budgets.toArray(),
      cashItems: await db.cashItems.toArray(),
      instanceOverrides: await db.instanceOverrides.toArray(),
      userSettings: await db.userSettings.toArray(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suitelifetools_budget_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async importBackup(file: File): Promise<boolean> {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      await db.transaction(
        'rw',
        db.budgets,
        db.cashItems,
        db.instanceOverrides,
        db.userSettings,
        async () => {
          if (data.budgets) await db.budgets.bulkPut(data.budgets);
          if (data.cashItems) await db.cashItems.bulkPut(data.cashItems);
          if (data.instanceOverrides) await db.instanceOverrides.bulkPut(data.instanceOverrides);
          if (data.userSettings) await db.userSettings.bulkPut(data.userSettings);
        },
      );

      const importedBudgets = await db.budgets.toArray();
      if (importedBudgets.length > 0) {
        const targetBudget = importedBudgets.find((b) => b.isDefault) || importedBudgets[0];
        this.selectBudget(targetBudget.id);
      }

      await this.triggerBackgroundSync();
      return true;
    } catch (err) {
      console.error('Failed to import backup file:', err);
      return false;
    }
  }

  private getStoredBudgetId(): string | null {
    return localStorage.getItem('slt_active_budget_id');
  }

  readonly activeDebtPlan: Signal<DebtPayoffPlan | null> = computed(() => {
    return this.activeSettings()?.activeDebtPlan ?? null;
  });

  async saveActiveDebtPlan(plan: DebtPayoffPlan): Promise<void> {
    const settings = this.activeSettings();
    if (!settings) return;

    await db.userSettings.update(settings.id, {
      activeDebtPlan: plan,
    });
    await this.triggerBackgroundSync();
  }

  async deleteActiveDebtPlan(budgetId: string): Promise<void> {
    const settings = await db.userSettings.where({ budgetId }).first();
    if (!settings) return;

    await db.userSettings.update(settings.id, {
      activeDebtPlan: null,
    });
    await this.triggerBackgroundSync();
  }

  async forceImmediateBackup(googleSub: string): Promise<void> {
    try {
      const backupPayload = {
        version: 4,
        exportedAt: new Date().toISOString(),
        budgets: await db.budgets.toArray(),
        cashItems: await db.cashItems.toArray(),
        instanceOverrides: await db.instanceOverrides.toArray(),
        userSettings: await db.userSettings.toArray(),
      };

      this.cloudBackup.saveBackup(googleSub, backupPayload).subscribe({
        next: () => {
          localStorage.setItem('slt_last_sync_timestamp', backupPayload.exportedAt);
          console.log('Immediate cloud backup sync successful upon opt-in.');
        },
        error: (err) => console.error('Immediate cloud backup failed:', err),
      });
    } catch (err) {
      console.error('Failed to compile immediate backup payload:', err);
    }
  }

  async initializeCloudSyncOnStartup(): Promise<void> {
    const isCloudBackupEnabled = localStorage.getItem('cloud_backup_opt_in') === 'true';
    if (!isCloudBackupEnabled) return;

    const user = this.auth.currentUser();
    const googleSub = user?.id;
    if (!googleSub) return;

    this.cloudBackup.getBackup(googleSub).subscribe({
      next: async (response) => {
        if (response && response.success && response.data) {
          await this.hydrateLocalDatabase(response.data);
          console.log('Application initialized: Local DB successfully hydrated from cloud backup.');
        }
      },
      error: (err) => {
        console.debug('Startup cloud backup pull skipped or unavailable:', err);
      },
    });
  }

  private async hydrateLocalDatabase(data: any): Promise<void> {
    await db.transaction(
      'rw',
      db.budgets,
      db.cashItems,
      db.instanceOverrides,
      db.userSettings,
      async () => {
        if (data.budgets) await db.budgets.bulkPut(data.budgets);
        if (data.cashItems) await db.cashItems.bulkPut(data.cashItems);
        if (data.instanceOverrides) await db.instanceOverrides.bulkPut(data.instanceOverrides);
        if (data.userSettings) await db.userSettings.bulkPut(data.userSettings);
      },
    );
  }

  async checkForRemoteUpdates(): Promise<void> {
    const isCloudBackupEnabled = localStorage.getItem('cloud_backup_opt_in') === 'true';
    if (!isCloudBackupEnabled) return;

    const user = this.auth.currentUser();
    if (!user?.id) return;

    this.cloudBackup.getBackup(user.id).subscribe({
      next: async (response) => {
        if (response && response.success && response.data) {
          const cloudExportedAt = new Date(response.data.exportedAt).getTime();
          const lastSyncTime = Number(localStorage.getItem('slt_last_sync_timestamp') || '0');

          if (cloudExportedAt > lastSyncTime) {
            const result = await this.messageService.showConfirm({
              title: 'Newer Backup Found',
              message: 'A newer backup was found from another device. Would you like to load it?',
              buttons: [
                { label: 'Ignore', value: 'no', closeOnClick: true },
                {
                  label: 'Load',
                  value: 'yes',
                  isPrimary: true,
                  closeOnClick: true,
                  isFocused: true,
                },
              ],
            });

            if (result === 'yes') {
              await this.hydrateLocalDatabase(response.data);
              localStorage.setItem('slt_last_sync_timestamp', cloudExportedAt.toString());
              window.location.reload();
            }
          }
        }
      },
      error: (err) => {
        console.debug('Remote update check skipped:', err);
      },
    });
  }
}
